import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Maybe } from '@graphql-tools/utils'
import classNames from 'classnames'
import { useSearchParams } from 'next/navigation'
import { Case, Default, Switch, When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { Breadcrumb } from '@/atoms/Breadcrumb'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { AsH3, TitleLG } from '@/atoms/Text'
import { projectTypes } from '@/constants/projectTypes'
import { ServerExperiments } from '@/experimentation'
import { Menu } from '@/molecules/Menu'
import { TIMELINE_ROLLUP_INTERVAL_MILLIS } from '@/organisms/AngelPlayer'
import { CollectiblesPlayer, PlayerMomentsProvider } from '@/organisms/CollectiblesPlayer'
import {
  CollectiblesPlayerEngagementProvider,
  EngagementDetail,
} from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { OneToManyContentSharingModal } from '@/organisms/CollectiblesPlayer/GuildContentLinking'
import { EpisodesSelector } from '@/organisms/EpisodesSelector'
import { HorizontalShopifyMerchScroller } from '@/organisms/HorizontalShopifyMerchScroller'
import { ProjectJourney } from '@/organisms/ProjectJourney'
import { ShareSection } from '@/organisms/ShareSection'
import { WatchEpisodeHero } from '@/organisms/WatchEpisodeHero'
import { getWebClient } from '@/services/ApolloClient'
import { useBotDetection } from '@/services/BotDetection'
import { CatalogTitle, hasHydraIdAndSlug } from '@/services/ContentCatalog'
import { ShopifyStoreConfig } from '@/services/EmailCapturePageService/EmailCapturePageService'
import {
  Episode,
  GET_EPISODE_AND_USER_WATCH_DATA_QUERY,
  GetEpisodeResponse,
  ProjectType,
  Season,
  useGetWatchableAvailabilityByGuid,
} from '@/services/ProjectsService'
import { useUser } from '@/services/UserService'
import { Moment } from '@/types/codegen-federation'
import { isATorch } from '@/utils/EpisodeUtil'
import { isTrailerOnlyProject } from '@/utils/SpecialProjectUtils/SpecialProjectUtils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { writeToSessionStorage } from '@/utils/session-storage'
import { AppPromoSection } from '@/views/LandingView/AppPromoSection'
import { Frame } from '../AccountView/tabs/CollectiblesTab/types'

export interface WatchEpisodeViewProps {
  autoplay?: boolean
  defaultMoment?: Maybe<Moment>
  defaultEngagementDetail?: Maybe<EngagementDetail>
  episode: Episode
  episodeId: string
  hasPayItForward?: boolean
  projectName: string
  projectSlug: string
  projectType: ProjectType
  publisherName?: string
  skipAds?: boolean
  skipAttribution?: boolean
  shopifyStore?: ShopifyStoreConfig
  userStoryId?: string
  frame?: Frame
  projectTitle?: CatalogTitle
  watchableTitle?: CatalogTitle
  isGuildMember?: boolean
  isSellingTickets: boolean
  canViewUnauthed?: boolean
  experiments?: ServerExperiments
}

export const WatchEpisodeView: React.FC<WatchEpisodeViewProps> = ({
  autoplay,
  defaultMoment,
  defaultEngagementDetail,
  episode,
  episodeId,
  projectName,
  projectSlug,
  projectType,
  skipAds,
  skipAttribution,
  hasPayItForward,
  shopifyStore,
  userStoryId,
  frame,
  projectTitle,
  watchableTitle,
  isGuildMember = false,
  isSellingTickets,
  canViewUnauthed = false,
  experiments,
}) => {
  const { user, userId, isLoggedIn } = useUser()
  const client = getWebClient()
  const { isBot } = useBotDetection()
  const { asPath } = useSafeRouter()
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null)
  const searchParams = useSearchParams()
  const [showShareModal, setShowShareModal] = useState(searchParams.has('share'))

  const { watchableContent } = useGetWatchableAvailabilityByGuid(episodeId, isLoggedIn, client)

  const { loading, data, error } = useQuery<GetEpisodeResponse>(GET_EPISODE_AND_USER_WATCH_DATA_QUERY, {
    nextFetchPolicy: 'standby',
    variables: {
      guid: episodeId,
      projectSlug: projectSlug,
      includePrerelease: true,
      authenticated: Boolean(userId),
      reactionsRollupInterval: TIMELINE_ROLLUP_INTERVAL_MILLIS,
    },
    errorPolicy: 'all',
  })

  const merchAnalytics = useMemo(() => {
    return { context: 'watch-episodes', content_id: episodeId, program: projectSlug }
  }, [episodeId, projectSlug])

  useEffect(() => {
    if (data?.project?.seasons) {
      const selectedSeason = data.project.seasons.find((season: Season) => {
        return season.id === data.episode?.seasonId
      })

      setSelectedSeason(selectedSeason || data.project.seasons[0] || null)
    }
  }, [data])

  useEffect(() => {
    writeToSessionStorage('pageSlug', 'watch-episode')
    if (projectSlug) writeToSessionStorage('projectSlugs', projectSlug)
  }, [projectSlug])

  const groups = user?.groups ?? []
  const validatedGroups = groups.filter((g) => g && g.name) as { name: string }[]
  const showPlayer = isLoggedIn || isBot || isTrailerOnlyProject(projectSlug)
  const isUnavailable = useMemo(() => {
    return !data?.episode?.unavailableReason
      ? false
      : Boolean(
          watchableContent &&
            !data?.episode?.isTrailer &&
            (watchableContent.watchableReasons?.length === 0 ||
              watchableContent?.watchableAvailabilityStatus === 'WATCHABLE_AT'),
        )
  }, [data, watchableContent])
  const isLoginRequired = canViewUnauthed ? false : !userId && !episode.isTrailer
  const isEpisodeEarlyAccess = useMemo(() => {
    const now = new Date()
    const earlyAccessActive = episode?.earlyAccessDate ? new Date(episode.earlyAccessDate) < now : false
    const publiclyAvailable = episode?.publiclyAvailableDate ? new Date(episode.publiclyAvailableDate) < now : false

    return Boolean(earlyAccessActive && !publiclyAvailable)
  }, [episode?.earlyAccessDate, episode?.publiclyAvailableDate])

  const canSeeGuildShareButton =
    isEpisodeEarlyAccess && watchableTitle && hasHydraIdAndSlug(watchableTitle) && isGuildMember
  const isGeoBlocked = useMemo(() => {
    const episodeGeoblocked = data?.episode?.unavailableReason === 'GEOBLOCKED'
    const projectRegionBlocked = data?.project?.title?.watchableAvailabilityStatus === 'REGION_BLOCKED'

    return episodeGeoblocked || projectRegionBlocked
  }, [data?.episode?.unavailableReason, data?.project?.title?.watchableAvailabilityStatus])
  const showEpisodeHero = Boolean(
    isUnavailable ||
      (isEpisodeEarlyAccess && !isLoggedIn) ||
      isGeoBlocked ||
      (isLoginRequired && Boolean(experiments && experiments['unauthed_watch_episode_hero'])),
  )

  return (
    <div
      className={classNames('relative w-full overflow-x-hidden bg-core-gray-950 text-white', {
        'pt-16': !showEpisodeHero,
      })}
    >
      {!showEpisodeHero && <Breadcrumb className="py-3" projectSlug={projectSlug} projectName={projectName} />}
      <PlayerMomentsProvider episodeGuid={episode.guid}>
        <CollectiblesPlayerEngagementProvider
          projectSlug={projectSlug}
          defaultEngagementDetail={defaultEngagementDetail}
          defaultMoment={defaultMoment}
          userStoryId={userStoryId}
        >
          <Switch>
            <Case condition={showEpisodeHero}>
              <WatchEpisodeHero
                loading={loading}
                data={data}
                episode={episode}
                hasPayItForward={hasPayItForward}
                slug={projectSlug}
                isGeoBlocked={isGeoBlocked}
                isEpisodeEarlyAccess={isEpisodeEarlyAccess}
                isLoginRequired={isLoginRequired}
                watchableContent={watchableContent}
              />
            </Case>
            <Case condition={showPlayer}>
              <CollectiblesPlayer
                data={data}
                episode={episode}
                defaultEngagementDetail={defaultEngagementDetail}
                shouldSaveWatchPosition
                skipAttribution={skipAttribution}
                skipAds={skipAds}
                autoplay={autoplay}
                frame={frame}
                defaultCommunityView="off"
                isLoginRequired={isLoginRequired}
                userId={userId}
                projectTitle={projectTitle}
                watchableTitle={watchableTitle}
                isSellingTickets={isSellingTickets}
                watchableContent={watchableContent}
                setShowShareModal={setShowShareModal}
              />
            </Case>
            <Default>
              <CollectiblesPlayer
                data={data}
                episode={episode}
                defaultEngagementDetail={defaultEngagementDetail}
                shouldSaveWatchPosition
                skipAttribution={skipAttribution}
                skipAds={skipAds}
                autoplay={autoplay}
                frame={frame}
                defaultCommunityView="off"
                isLoginRequired={isLoginRequired}
                userId={userId}
                projectTitle={projectTitle}
                watchableTitle={watchableTitle}
                isSellingTickets={isSellingTickets}
                watchableContent={watchableContent}
                setShowShareModal={setShowShareModal}
              />
            </Default>
          </Switch>
          <OneToManyContentSharingModal
            projectSlug={episode.projectSlug}
            projectTitle={projectTitle}
            watchableTitle={watchableTitle}
            episodeId={episode.id}
            showShareModal={showShareModal}
            setShowShareModal={setShowShareModal}
          />
        </CollectiblesPlayerEngagementProvider>
      </PlayerMomentsProvider>
      <div className="relative overflow-x-hidden bg-core-gray-950 pb-32">
        {isATorch(episode) && (
          <>
            <hr className="my-4 border-gray-800" />
            <PaddedContainer fluid>
              <ProjectJourney
                projectSlug={projectSlug}
                className="mx-auto max-w-[1292px] px-6 py-16 text-white sm:px-12 2xl:px-0"
              />
            </PaddedContainer>
          </>
        )}
        <When condition={!canSeeGuildShareButton}>
          <LazyHydrate whenVisible>
            <PaddedContainer fluid>
              <ShareSection
                projectName={projectName}
                theatricalSlug={projectSlug}
                shareUrl={asPath}
                projectType={projectType}
                className="ml-0 flex flex-col !items-start bg-core-gray-950 pt-12 !text-left"
                headerClassName="photon-title-sm"
              />
            </PaddedContainer>
          </LazyHydrate>
        </When>
        {shopifyStore && (
          <LazyHydrate whenVisible>
            <>
              <hr className="my-8 border-gray-800" />
              <HorizontalShopifyMerchScroller
                storeUrl={shopifyStore.storeUrl}
                accessToken={shopifyStore.accessToken}
                handle={shopifyStore.handle}
                storeHref={shopifyStore?.storeHref}
                analytics={merchAnalytics}
                slug={projectSlug}
                padding
              />
            </>
          </LazyHydrate>
        )}
        <hr className="my-4 border-gray-800" />
        <PaddedContainer fluid>
          {projectType !== projectTypes.movie && (
            <section className="mt-8 w-full">
              <TitleLG weight="bold" as={AsH3} className="mb-3 md:mb-4">
                Episodes
              </TitleLG>
              {data?.project?.projectType !== projectTypes.movie && (
                <LazyHydrate whenVisible>
                  <Menu
                    menuClassName="w-full sm:w-fit mb-2 sm:mb-0 mr-6"
                    options={data?.project?.seasons as Season[]}
                    value={selectedSeason || undefined}
                    onChange={setSelectedSeason}
                  />
                </LazyHydrate>
              )}
              <LazyHydrate whenVisible>
                <EpisodesSelector
                  hasError={!!error}
                  loading={loading}
                  selectedSeason={selectedSeason as Season}
                  projectSlug={projectSlug}
                  projectName={projectName}
                  projectType={projectType}
                  userGroups={validatedGroups}
                />
              </LazyHydrate>
            </section>
          )}

          <LazyHydrate whenVisible>
            <AppPromoSection />
          </LazyHydrate>
        </PaddedContainer>
      </div>
    </div>
  )
}
